<script setup lang="ts">
import type { Suggestion } from 'types/models/product'
import { useDebounceFn } from '@vueuse/core'
import { productUrl } from 'lib/routing'
import { ComboboxAnchor, ComboboxContent, ComboboxEmpty, ComboboxGroup, ComboboxInput, ComboboxItem, ComboboxRoot } from 'reka-ui'

const emit = defineEmits(['searchExpand', 'searchCollapse', 'closeMenu'])
const SEARCH_INPUT_MINIMUM = 3
const { $t } = useNuxtApp()

const searchStore = useSearchStore()
const router = useRouter()
const gtm = useGTM()
const { $currentStorefront } = useNuxtApp()

const suggestions = ref<Suggestion[] | []>([])
const searchOpen = ref(false)
const searchInput = ref('')
const loading = ref(false)

function onOpenSearch(open: boolean) {
  searchOpen.value = open

  if (open) {
    emit('searchExpand')
  }
  else {
    emit('searchCollapse')
  }
}

const onInput = useDebounceFn(async (input: string) => {
  searchInput.value = input
  loading.value = true

  if (input.length < SEARCH_INPUT_MINIMUM) {
    suggestions.value = []
  }
  else {
    try {
      const data = await searchStore.fetchSuggestions(input)
      suggestions.value = data.products ?? []
    }
    catch (error: any) {
      if (error && error.status) // http error only
        console.error(error)
    }
    finally {
      loading.value = false
    }
  }
}, 500)

async function onSelect(suggestion: Suggestion | undefined) {
  try {
    if (suggestion) {
      gtm.pushClickSearch(searchInput.value, suggestion.id, 'suggestionClick')
      await router.push(productUrl(suggestion.id))
    }
    else {
      gtm.pushClickSearch(searchInput.value, '', 'searchClick')
      await router.push(
        `/${$currentStorefront.storefrontCode}/search?term=${encodeURI(searchInput.value)}`,
      )
    }
  }
  catch (error) {
    console.error(error)
  }
  finally {
    emit('searchCollapse')
    emit('closeMenu')
    searchOpen.value = false
  }
}
</script>

<template>
  <ComboboxRoot
    :open-on-focus="true"
    :open-on-click="true"
    :open="searchOpen"
    class="ComboboxRoot"
    :ignore-filter="true"
    @update:open="onOpenSearch"
  >
    <ComboboxAnchor
      class="ComboboxAnchor"
      :class="[{
        expanded: searchOpen,
      }]"
    >
      <ComboboxInput
        class="ComboboxInput"
        :placeholder="$t('search')"
        @update:model-value="onInput"
      />
    </ComboboxAnchor>
    <ComboboxContent
      class="ComboboxContent"
    >
      <ComboboxViewport class="ComboboxViewport">
        <ComboboxEmpty
          v-if="searchInput.length >= SEARCH_INPUT_MINIMUM && !loading && suggestions.length === 0"
          class="ComboboxEmpty"
        >
          {{ $t('searchNoResultsTitle', { term: searchInput }) }}
        </ComboboxEmpty>
        <ComboboxGroup v-if="suggestions.length">
          <ComboboxItem
            v-if="suggestions.length > 0"
            data-test="search-suggestion"
            class="ComboboxItem showMore"
            :value="null"
            @select="onSelect(undefined)"
          >
            <span>
              {{ $t('showMore') }}
            </span>
          </ComboboxItem>
          <ComboboxItem
            v-for="suggestion in suggestions"
            :key="suggestion.id"
            data-test="search-suggestion"
            class="ComboboxItem"
            :value="suggestion"
            @select="() => onSelect(suggestion)"
          >
            <ImageResponsive
              provider="fastly"
              :src="suggestion?.product?.photo ?? ''"
              alt=""
              :max-width="40"
              ratio="1:1"
            />
            <span>
              {{ suggestion.product.title }}
            </span>
          </ComboboxItem>
        </ComboboxGroup>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
</template>

<style lang="scss" scoped>
.expanded {
  background-color: white;
  border: 2px solid;
}

.showMore {
  display: flex;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--gray);
  opacity: 0.9;
}

.showMore[data-highlighted] {
  background-color: var(--green);
  opacity: 1;
}
</style>
